import React from 'react';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useA11yTokens } from '../hooks/useA11yTokens';
import { A11yContrast, setA11yContrast, setA11yLinkHighlight, resetA11y } from '../store/mainSlice';
import { useAppSelector } from '../store/hooks';
import { A11yNormalContrastIcon, A11yHighContrastIcon, A11yBlackAndWhiteIcon, A11yLinkHighlightIcon } from './A11yIcons';

interface A11yDrawerProps {
    open: boolean;
    onClose: () => void;
}

const A11yDrawer: React.FC<A11yDrawerProps> = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const a11yContrast = useAppSelector((state) => state.main.a11yContrast);
    const a11yLinkHighlight = useAppSelector((state) => state.main.a11yLinkHighlight);
    const isHighContrast = a11yContrast === 'high';
    const theme4dTokens = useA11yTokens();
    const drawerRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) onClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open, onClose]);

    if (!open) return null;

    const iconColor = isHighContrast ? theme4dTokens.text : undefined;

    const contrastOptions: { value: A11yContrast; label: string; icon: React.FC<{ color?: string }> }[] = [
        { value: 'blackAndWhite', label: t('a11y.contrastBlackAndWhite'), icon: A11yBlackAndWhiteIcon },
        { value: 'high', label: t('a11y.contrastHigh'), icon: A11yHighContrastIcon },
        { value: 'normal', label: t('a11y.contrastNormal'), icon: A11yNormalContrastIcon },
    ];

    const rowSx = (selected: boolean) => ({
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        border: `2px solid ${selected ? theme4dTokens.text : 'transparent'}`,
        borderRadius: '6px',
        background: 'none',
        px: '12px',
        fontSize: '14px',
        color: theme4dTokens.text,
        cursor: 'pointer',
        textAlign: 'start',
        '&:hover': { border: `2px solid ${theme4dTokens.text}` },
    });

    const sectionHeaderSx = {
        height: '50px',
        bgcolor: isHighContrast ? theme4dTokens.surface : '#dddddd',
        border: isHighContrast ? `1px solid ${theme4dTokens.border}` : 'none',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        px: '12px',
        color: isHighContrast ? theme4dTokens.text : '#000',
    } as const;

    return (
        <Box
            ref={drawerRef}
            sx={{
                position: 'fixed',
                insetInlineEnd: 0,
                top: 0,
                height: '100%',
                width: '300px',
                maxWidth: '85vw',
                overflow: 'auto',
                bgcolor: theme4dTokens.surface,
                color: theme4dTokens.text,
                zIndex: 1300,
                borderInlineStart: isHighContrast ? `1px solid ${theme4dTokens.border}` : 'none',
                boxShadow: isHighContrast ? 'none' : '0 0 25px rgba(0,0,0,.26)',
            }}
        >
            <Box sx={{ height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '12px' }}>
                <Box component="span" sx={{ fontSize: '22px', fontWeight: 400 }}>
                    {t('a11y.menuTitle')}
                </Box>
                <Box
                    component="button"
                    type="button"
                    onClick={onClose}
                    sx={{ border: 'none', background: 'none', fontSize: '20px', color: theme4dTokens.text, cursor: 'pointer' }}
                >
                    ✕
                </Box>
            </Box>

            <Box sx={sectionHeaderSx}>{t('a11y.contrastSection')}</Box>
            {contrastOptions.map((opt) => (
                <Box
                    key={opt.value}
                    component="button"
                    type="button"
                    onClick={() => dispatch(setA11yContrast(opt.value))}
                    sx={rowSx(a11yContrast === opt.value)}
                >
                    <opt.icon color={iconColor} />
                    {opt.label}
                </Box>
            ))}

            <Box sx={sectionHeaderSx}>{t('a11y.moreSettings')}</Box>
            <Box
                component="button"
                type="button"
                onClick={() => dispatch(setA11yLinkHighlight(!a11yLinkHighlight))}
                sx={rowSx(a11yLinkHighlight)}
            >
                <A11yLinkHighlightIcon color={iconColor} />
                {t('a11y.linkHighlight')}
            </Box>
            <Box
                component="button"
                type="button"
                onClick={() => {
                    dispatch(resetA11y());
                    onClose();
                }}
                sx={rowSx(false)}
            >
                <Box component="span" sx={{ width: '20px', textAlign: 'center', fontSize: '16px' }}>
                    ✕
                </Box>
                {t('a11y.reset')}
            </Box>
        </Box>
    );
};

export default A11yDrawer;
